<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import Data from '@/api/data';
import { useModalBackdropClose } from '@/composables/useModalBackdropClose';
import { applyTransparentBackgroundFromBlob } from '@/tools/imageTransparentBackground';
import { getClassIconUrl } from '@/tools/classIconUrl';
import {
  normalizeQuickMetricSpecs,
  flattenTimeseriesStyleSpecs,
  createEmptyQuickMetric,
  serializeMapAnimationRule,
  validateAnimationRuleOverlaps,
} from '@/composables/useQuickMetrics';
import QuickMetricRow from '@/components/common/QuickMetricRow.vue';

const props = defineProps({
  units: {
    type: Array,
    default: () => []
  },
  unitsLoading: {
    type: Boolean,
    default: false
  },
  /** When set, modal is in edit mode (pre-fill and emit classId on submit) */
  classDetails: {
    type: Object,
    default: null
  },
  submitting: {
    type: Boolean,
    default: false
  },
  submitError: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close', 'submit']);

const isEditMode = computed(() => !!props.classDetails?.name);

const name = ref('');
const description = ref('');
const quickMetrics = ref([]);
const quickMetricSourceOptions = ref({});
const quickMetricFieldOptions = ref({});
const quickMetricsLoading = ref(false);
const quickMetricsLoadError = ref('');
let quickMetricsAbortController = null;
const referenceUnitId = ref('');
const selectedUnitIds = ref([]);
const searchQuery = ref('');
const sortColumn = ref(null);
const sortDirection = ref('asc');
const error = ref('');
// Icon state
const iconFile = ref(null);       // File object from upload
const iconUrl = ref('');          // URL from AI generation or existing
const iconPreview = ref('');      // Whatever is shown in the preview box
const iconInputRef = ref(null);

// Icon generation state
const aiPrompt = ref('');
const aiGenerating = ref(false);
const aiError = ref('');
const showAiInput = ref(false);

const quickMetricSourceNames = computed(() =>
  Object.keys(quickMetricSourceOptions.value).sort((a, b) => a.localeCompare(b))
);

const allUnitOptions = computed(() => (props.units || []).map((unit) => ({
  id: unit.dataID,
  name: unit.friendlyName || unit.dataID,
})));

const filteredSortedUnits = computed(() => {
  let result = allUnitOptions.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    result = result.filter((u) =>
      (u.name ?? '').toLowerCase().includes(q) || (u.id ?? '').toLowerCase().includes(q)
    );
  }
  if (sortColumn.value) {
    result = [...result].sort((a, b) => {
      const av = a[sortColumn.value];
      const bv = b[sortColumn.value];
      const cmp = String(av ?? '').localeCompare(String(bv ?? ''));
      return sortDirection.value === 'asc' ? cmp : -cmp;
    });
  }
  return result;
});

const toggleSort = (column) => {
  if (sortColumn.value === column) {
    if (sortDirection.value === 'asc') {
      sortDirection.value = 'desc';
    } else {
      sortColumn.value = null;
      sortDirection.value = 'asc';
    }
  } else {
    sortColumn.value = column;
    sortDirection.value = 'asc';
  }
};

const toggleUnit = (unitId) => {
  const idx = selectedUnitIds.value.indexOf(unitId);
  if (idx === -1) selectedUnitIds.value.push(unitId);
  else selectedUnitIds.value.splice(idx, 1);
};

const referenceUnitOptions = computed(() => {
  if (selectedUnitIds.value.length === 0) return allUnitOptions.value;
  const allowed = new Set(selectedUnitIds.value);
  return allUnitOptions.value.filter((unit) => allowed.has(unit.id));
});

function ensureReferenceUnit() {
  const candidateIds = selectedUnitIds.value.length > 0
    ? selectedUnitIds.value
    : referenceUnitOptions.value.map((u) => u.id);

  if (!candidateIds.length) {
    referenceUnitId.value = '';
    quickMetricSourceOptions.value = {};
    quickMetricFieldOptions.value = {};
    return;
  }

  if (!candidateIds.includes(referenceUnitId.value)) {
    referenceUnitId.value = candidateIds[0];
  }
}

async function loadQuickMetricSourceOptions() {
  if (quickMetricsAbortController) {
    quickMetricsAbortController.abort();
    quickMetricsAbortController = null;
  }

  if (!referenceUnitId.value) {
    quickMetricSourceOptions.value = {};
    quickMetricFieldOptions.value = {};
    quickMetricsLoading.value = false;
    return;
  }

  const controller = new AbortController();
  quickMetricsAbortController = controller;
  const { signal } = controller;

  quickMetricsLoading.value = true;
  quickMetricsLoadError.value = '';
  try {
    const response = await Data.get(referenceUnitId.value, null, { signal });
    if (response?.body?.status !== 'success') {
      quickMetricsLoadError.value = 'Could not load telemetry fields for the selected reference unit.';
      return;
    }

    const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages'];
    const sourceOptions = Object.fromEntries(
      Object.entries(response.body.details || {})
        .filter(([key]) => !ignoreSubcollections.includes(key))
        .sort(([a], [b]) => a.localeCompare(b))
    );

    quickMetricSourceOptions.value = sourceOptions;
    const nextFieldOptions = {};
    Object.entries(sourceOptions).forEach(([source, fields]) => {
      nextFieldOptions[source] = Object.entries(fields || {})
        .filter(([, type]) => type !== 'object' && type !== 'array' && type !== 'list' && type !== 'dict')
        .map(([field]) => field)
        .sort((a, b) => a.localeCompare(b));
    });
    quickMetricFieldOptions.value = nextFieldOptions;

    // Clear invalid source/field choices if reference device changed.
    quickMetrics.value = quickMetrics.value.map((metric) => {
      if (!metric.source) return metric;
      if (!nextFieldOptions[metric.source]) {
        return { ...metric, source: '', field: '' };
      }
      if (metric.field && !nextFieldOptions[metric.source].includes(metric.field)) {
        return { ...metric, field: '' };
      }
      return metric;
    });
  } catch (err) {
    if (err?.name === 'AbortError') return;
    console.error('Error loading quick metric source options:', err);
    quickMetricsLoadError.value = 'Could not load telemetry fields for the selected reference unit.';
  } finally {
    if (quickMetricsAbortController === controller) {
      quickMetricsAbortController = null;
      quickMetricsLoading.value = false;
    }
  }
}

function addQuickMetric() {
  quickMetrics.value = [...quickMetrics.value, createEmptyQuickMetric()];
}

function removeQuickMetric(index) {
  quickMetrics.value = quickMetrics.value.filter((_, i) => i !== index);
}

// Sync form fields only when classDetails changes (not when units change, to avoid clearing inputs while typing)
watch(
  () => props.classDetails,
  () => {
    name.value = props.classDetails?.name || '';
    description.value = props.classDetails?.description || '';
    const existingQuickMetrics =
      props.classDetails?.quickMetrics ||
      props.classDetails?.quickMetricSpecs ||
      props.classDetails?.dashboard?.quickMetrics ||
      flattenTimeseriesStyleSpecs(props.classDetails?.quickMetricSpecifications || props.classDetails?.specifications);
    const normalizedQuickMetrics = normalizeQuickMetricSpecs(existingQuickMetrics);
    quickMetrics.value = normalizedQuickMetrics.length
      ? normalizedQuickMetrics
      : [];
    iconFile.value = null;
    const existingIcon = getClassIconUrl(props.classDetails);
    iconUrl.value = existingIcon;
    iconPreview.value = existingIcon;
    aiPrompt.value = '';
    aiError.value = '';
    showAiInput.value = false;
  },
  { immediate: true }
);

// Sync unit pre-selection when classDetails or units change
watch(
  () => props.classDetails,
  () => {
    const unitIds = props.classDetails?.unitIds || [];
    selectedUnitIds.value = (props.units || [])
      .filter((u) => unitIds.includes(u.dataID))
      .map((u) => u.dataID);
  },
  { immediate: true }
);

watch(
  () => [selectedUnitIds.value, props.units],
  () => {
    ensureReferenceUnit();
  },
  { immediate: true, deep: true }
);

watch(
  () => referenceUnitId.value,
  () => {
    loadQuickMetricSourceOptions();
  },
  { immediate: true }
);

function onIconSelected(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  iconFile.value = file;
  iconUrl.value = '';
  iconPreview.value = URL.createObjectURL(file);
  showAiInput.value = false;
  aiError.value = '';
}

function removeIcon() {
  iconFile.value = null;
  iconUrl.value = '';
  iconPreview.value = '';
  aiError.value = '';
  showAiInput.value = false;
  if (iconInputRef.value) iconInputRef.value.value = '';
}

async function generateAiIcon() {
  const prompt = aiPrompt.value.trim();
  if (!prompt) return;
  aiGenerating.value = true;
  aiError.value = '';

  const key = process.env.VUE_APP_POLLINATIONS_API_KEY;
  if (!key) {
    aiError.value = 'Missing VUE_APP_POLLINATIONS_API_KEY in .env.local';
    aiGenerating.value = false;
    return;
  }

  // New Pollinations API: GET https://gen.pollinations.ai/image/{prompt}?key=...&model=... (model is required)
  const styledPrompt = `simple minimalist ${prompt} icon, flat vector, single subject centered, isolated cutout, solid flat white background, no shadow, no frame, png`;
  const pathPrompt = encodeURIComponent(styledPrompt);
  const seed = Math.floor(Math.random() * 1000000);
  const models = ['flux', 'turbo', 'nanobanana', 'klein'];
  const isDev = process.env.NODE_ENV === 'development';

  const buildUrl = (model) => {
    const qs = new URLSearchParams({
      width: '512',
      height: '512',
      model,
      nologo: 'true',
      seed: String(seed),
      format: 'png',
    });
    if (!isDev) qs.set('key', key);
    const path = `/image/${pathPrompt}?${qs.toString()}`;
    return isDev ? `/__pollinations_image${path}` : `https://gen.pollinations.ai${path}`;
  };

  let loadedUrl = null;
  for (const model of models) {
    const url = buildUrl(model);
    const ok = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
    if (ok) {
      loadedUrl = url;
      break;
    }
  }

  if (loadedUrl) {
    try {
      const res = await fetch(loadedUrl);
      if (res.ok) {
        const rawBlob = await res.blob();
        const pngBlob = await applyTransparentBackgroundFromBlob(rawBlob, {
          tolerance: 42,
          feather: 18,
        });
        const file = new File([pngBlob], `ai-icon-${Date.now()}.png`, {
          type: 'image/png',
        });
        iconFile.value = file;
        iconUrl.value = '';
        iconPreview.value = URL.createObjectURL(pngBlob);
      } else {
        iconFile.value = null;
        iconUrl.value = loadedUrl;
        iconPreview.value = loadedUrl;
      }
    } catch {
      iconFile.value = null;
      iconUrl.value = loadedUrl;
      iconPreview.value = loadedUrl;
    }
    showAiInput.value = false;
    if (iconInputRef.value) iconInputRef.value.value = '';
  } else {
    aiError.value = 'Generation failed. Try a different prompt or upload an image instead.';
  }

  aiGenerating.value = false;
}

function close() {
  if (quickMetricsAbortController) {
    quickMetricsAbortController.abort();
    quickMetricsAbortController = null;
  }
  name.value = '';
  description.value = '';
  quickMetrics.value = [];
  quickMetricSourceOptions.value = {};
  quickMetricFieldOptions.value = {};
  quickMetricsLoading.value = false;
  quickMetricsLoadError.value = '';
  referenceUnitId.value = '';
  selectedUnitIds.value = [];
  searchQuery.value = '';
  sortColumn.value = null;
  sortDirection.value = 'asc';
  error.value = '';
  iconFile.value = null;
  iconUrl.value = '';
  iconPreview.value = '';
  aiPrompt.value = '';
  aiError.value = '';
  showAiInput.value = false;
  emit('close');
}

const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);

function handleSubmit() {
  if (props.submitting) return;
  error.value = '';
  const trimmedName = (name.value || '').trim();
  if (!trimmedName) {
    error.value = 'Please enter a class name.';
    return;
  }
  const unitIds = selectedUnitIds.value;
  const payload = {
    name: trimmedName,
    description: (description.value || '').trim(),
    unitIds,
  };

  const normalizedQuickMetrics = normalizeQuickMetricSpecs(quickMetrics.value);
  const hasPartiallyFilledMetric = quickMetrics.value.some((metric) => {
    const hasAnyValue = !!(
      (metric.source || '').trim() ||
      (metric.field || '').trim() ||
      (metric.friendlyName || '').trim() ||
      (metric.unit || '').trim()
    );
    if (!hasAnyValue) return false;
    return !((metric.source || '').trim() && (metric.field || '').trim());
  });
  if (hasPartiallyFilledMetric) {
    error.value = 'Each quick metric row must include both Source and Field.';
    return;
  }
  const serializedQuickMetrics = [];
  for (const metric of normalizedQuickMetrics) {
    const serializedRule = serializeMapAnimationRule(metric.mapAnimationRule);
    if (serializedRule?.error) {
      error.value = `Quick metric "${metric.friendlyName || metric.field}": ${serializedRule.error}`;
      return;
    }
    serializedQuickMetrics.push({
      source: metric.source,
      field: metric.field,
      friendlyName: metric.friendlyName,
      unit: metric.unit,
      ...(serializedRule ? { mapAnimationRule: serializedRule } : {}),
    });
  }
  const overlapError = validateAnimationRuleOverlaps(serializedQuickMetrics);
  if (overlapError) {
    error.value = overlapError;
    return;
  }
  payload.quickMetrics = serializedQuickMetrics;

  let icon = iconFile.value;
  if (!icon && iconUrl.value?.trim()) {
    icon = iconUrl.value.trim();
  }
  if (icon) {
    payload.icon = icon;
  }
  if (isEditMode.value && props.classDetails?.id) {
    payload.classId = props.classDetails.id;
  }
  emit('submit', payload);
}
</script>

<template>
  <div
    class="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
    @mousedown.self="onBackdropMousedown"
    @mouseup.self="onBackdropMouseup"
  >
    <div
      class="ui-modal rounded-mc-lg overflow-hidden w-full max-w-[800px] max-h-[calc(100vh-2rem)] overflow-y-auto shadow-2xl"
      @mousedown="onContentMousedown"
    >
      <!-- Red accent bar -->
      <div class="px-4 sm:px-6 pt-5">
        <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
      </div>
      <!-- Header -->
      <div class="px-4 sm:px-6 py-5 border-b border-mc-steel dark:border-white/10 flex items-center gap-3">
        <div class="w-9 h-9 rounded-full bg-mc-red/10 flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-mc-red" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <rect x="2" y="7" width="20" height="14" rx="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M16 3H8l-2 4h12l-2-4z"/>
          </svg>
        </div>
        <div>
          <h2 class="font-extrabold text-xl leading-tight text-mc-ink dark:text-on-dark tracking-tight">
            {{ isEditMode ? 'Edit class' : 'Create class' }}
          </h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {{ isEditMode ? 'Update device class details and assignments' : 'Define a new device class with metrics and icon' }}
          </p>
        </div>
      </div>
      <!-- Body -->
      <div class="p-4 sm:p-6">
        <div class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4">
            <div>
              <label class="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Name *</label>
              <input
                v-model="name"
                type="text"
                class="input-base dark:bg-mc-charcoal dark:text-on-dark"
                placeholder="e.g. Tractor"
              />
            </div>
            <div>
              <label class="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Description</label>
              <textarea
                v-model="description"
                rows="2"
                class="input-base dark:bg-mc-charcoal dark:text-on-dark"
                placeholder="Optional"
              />
            </div>
          </div>
          <div class="rounded-mc-sm border border-mc-steel dark:border-white/10 bg-mc-cloud dark:bg-white/5 p-4">
            <div class="flex items-center gap-1.5 mb-1">
              <span class="w-0.5 h-3.5 bg-mc-red rounded-full flex-shrink-0"></span>
              <span class="text-[10px] font-bold uppercase tracking-widest text-mc-red">Quick Metrics</span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Pick telemetry fields to show on class unit cards (no JSON needed).
            </p>
            <div class="space-y-3">
              <div class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <label class="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Reference Unit</label>
                  <select
                    v-model="referenceUnitId"
                    class="input-base dark:bg-mc-charcoal dark:text-on-dark"
                  >
                    <option value="" disabled>Select a unit...</option>
                    <option v-for="unit in referenceUnitOptions" :key="unit.id" :value="unit.id">
                      {{ unit.name }} ({{ unit.id }})
                    </option>
                  </select>
                </div>
                <button
                  type="button"
                  @click="addQuickMetric"
                  class="btn-secondary text-sm px-3 py-1.5"
                  :disabled="quickMetricsLoading || !referenceUnitId"
                >
                  + Add Metric
                </button>
              </div>

              <p v-if="quickMetricsLoading" class="text-xs text-gray-500 dark:text-gray-400">Loading available telemetry fields...</p>
              <p v-else-if="quickMetricsLoadError" class="text-xs text-red-500 dark:text-red-400">{{ quickMetricsLoadError }}</p>
              <p v-else-if="referenceUnitId && Object.keys(quickMetricSourceOptions).length === 0" class="text-xs text-gray-500 dark:text-gray-400">
                No telemetry sources available for the selected reference unit.
              </p>

              <div v-if="quickMetrics.length > 0" class="space-y-2">
                <QuickMetricRow
                  v-for="(metric, index) in quickMetrics"
                  :key="`quick-metric-${index}`"
                  :metric="metric"
                  :source-names="quickMetricSourceNames"
                  :field-options="quickMetricFieldOptions"
                  :field-types="quickMetricSourceOptions"
                  @update:metric="quickMetrics[index] = $event"
                  @remove="removeQuickMetric(index)"
                />
              </div>

              <p v-else class="text-[11px] text-gray-400 dark:text-gray-500">
                No quick metrics configured. Unit cards will use automatic metric detection.
              </p>
            </div>
          </div>
          <!-- Map Icon -->
          <div class="rounded-mc-sm border border-mc-steel dark:border-white/10 bg-mc-cloud dark:bg-white/5 p-4">
            <div class="flex items-center gap-1.5 mb-3">
              <span class="w-0.5 h-3.5 bg-mc-red rounded-full flex-shrink-0"></span>
              <span class="text-[10px] font-bold uppercase tracking-widest text-mc-red">Map Icon</span>
            </div>
            <div class="flex items-start gap-4">

              <!-- Preview box -->
              <div class="w-16 h-16 rounded-lg border-2 border-dashed border-mc-steel dark:border-white/20 flex items-center justify-center overflow-hidden flex-shrink-0 bg-mc-cloud dark:bg-white/5 relative">
                <img v-if="iconPreview && !aiGenerating" :src="iconPreview" class="w-full h-full object-contain p-1" alt="icon preview" />
                <div v-else-if="aiGenerating" class="flex flex-col items-center gap-1">
                  <svg class="w-5 h-5 animate-spin text-mc-red" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                  </svg>
                  <span class="text-[9px] text-gray-400 text-center leading-tight">Generating...</span>
                </div>
                <svg v-else class="w-7 h-7 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4-4a3 3 0 014.24 0L16 16m-2-2l1.586-1.586a3 3 0 014.243 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>

              <!-- Actions column -->
              <div class="flex flex-col gap-2 flex-grow min-w-0">
                <!-- Upload + AI Generate buttons -->
                <div class="flex flex-wrap gap-2">
                  <label class="cursor-pointer btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-sm">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {{ iconPreview ? 'Replace' : 'Upload' }}
                    <input ref="iconInputRef" type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml" class="hidden" @change="onIconSelected" />
                  </label>

                  <button
                    type="button"
                    @click="showAiInput = !showAiInput; aiError = ''"
                    class="btn-commerce inline-flex items-center gap-1.5 px-3 py-1.5 text-sm"
                  >
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l2.4 7.2H22l-6.2 4.5 2.4 7.2L12 16.4l-6.2 4.5 2.4-7.2L2 9.2h7.6z"/>
                    </svg>
                    Generate Icon
                  </button>
                </div>

                <!-- Gemini prompt panel -->
                <div v-if="showAiInput" class="flex flex-col gap-1.5">
                  <div class="flex gap-2">
                    <input
                      v-model="aiPrompt"
                      type="text"
                      placeholder='e.g. "red tractor" or "blue water sensor"'
                      class="input-base dark:bg-mc-charcoal dark:text-on-dark flex-grow"
                      @keydown.enter.prevent="generateAiIcon"
                      :disabled="aiGenerating"
                    />
                    <button
                      type="button"
                      @click="generateAiIcon"
                      :disabled="aiGenerating || !aiPrompt.trim()"
                      class="btn-primary flex-shrink-0"
                    >
                      <svg v-if="aiGenerating" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                      </svg>
                      {{ aiGenerating ? 'Generating...' : 'Generate' }}
                    </button>
                  </div>
                  <p v-if="aiError" class="text-xs text-red-500 dark:text-red-400">{{ aiError }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500">Powered by Pollinations.AI</p>
                </div>

                <!-- Remove + hint -->
                <div class="flex items-center gap-3">
                  <button v-if="iconPreview" type="button" @click="removeIcon" class="text-xs text-red-500 dark:text-red-400 hover:underline">
                    Remove icon
                  </button>
                  <p v-else class="text-xs text-gray-400 dark:text-gray-500">PNG, JPG, SVG, WebP · Shown as map marker</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="px-4 sm:px-6 pb-2">
        <div class="flex items-center gap-1.5 mb-1">
          <span class="w-0.5 h-3.5 bg-mc-red rounded-full flex-shrink-0"></span>
          <span class="text-[10px] font-bold uppercase tracking-widest text-mc-red">Unit Assignments</span>
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400">Select units that belong to this class.</p>
      </div>
      <div class="px-4 sm:px-6 pb-4">
        <!-- Search bar -->
        <div class="relative mb-2">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name or ID..."
            class="w-full pl-8 pr-3 py-2 border border-mc-steel dark:border-white/10 rounded-mc-sm bg-mc-white dark:bg-white/5 focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20 text-sm"
          />
          <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">⌕</span>
        </div>
        <!-- Sortable unit table -->
        <div class="border border-gray-300 dark:border-gray-600 rounded-mc-sm h-[260px] overflow-y-auto">
          <table class="w-full border-collapse">
            <thead class="bg-gray-50 dark:bg-gray-800 sticky top-0">
              <tr>
                <th class="w-8 p-2"></th>
                <th
                  class="text-left p-2 text-xs uppercase tracking-wide cursor-pointer select-none"
                  :class="sortColumn === 'id' ? 'text-mc-red' : 'text-gray-500 dark:text-gray-400'"
                  @click="toggleSort('id')"
                >
                  ID <span>{{ sortColumn === 'id' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                </th>
                <th
                  class="text-left p-2 text-xs uppercase tracking-wide cursor-pointer select-none"
                  :class="sortColumn === 'name' ? 'text-mc-red' : 'text-gray-500 dark:text-gray-400'"
                  @click="toggleSort('name')"
                >
                  Name <span>{{ sortColumn === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                </th>
              </tr>
            </thead>
            <tbody v-if="!unitsLoading">
              <tr
                v-for="unit in filteredSortedUnits"
                :key="unit.id"
                class="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer"
                :class="selectedUnitIds.includes(unit.id) ? 'bg-mc-red-100 dark:bg-red-900/10' : ''"
                @click="toggleUnit(unit.id)"
              >
                <td class="p-2 text-center">
                  <input
                    type="checkbox"
                    :checked="selectedUnitIds.includes(unit.id)"
                    @click.stop
                    @change="toggleUnit(unit.id)"
                  />
                </td>
                <td class="p-2 text-sm text-gray-500 dark:text-gray-400">{{ unit.id }}</td>
                <td class="p-2 text-sm">{{ unit.name }}</td>
              </tr>
              <tr v-if="filteredSortedUnits.length === 0">
                <td colspan="3" class="p-4 text-center text-sm text-gray-400">
                  No units match your search.
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="3" class="p-4 text-center text-sm text-gray-400">
                  Loading units...
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Footer -->
        <div class="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
          <span>Showing {{ filteredSortedUnits.length }} of {{ allUnitOptions.length }}</span>
          <span>Selected: {{ selectedUnitIds.length }} unit(s)</span>
        </div>
      </div>

      <div v-if="error || submitError" class="px-4 sm:px-6 pb-2 space-y-1">
        <p v-if="error" class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
        <p v-if="submitError" class="text-sm text-red-600 dark:text-red-400">{{ submitError }}</p>
      </div>

      <div class="p-4 sm:p-6 flex justify-end gap-2 border-t border-mc-steel dark:border-white/10">
        <button
          type="button"
          :disabled="submitting"
          @click="close"
          class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160 disabled:opacity-50 disabled:pointer-events-none"
        >
          Cancel
        </button>
        <button
          type="button"
          :disabled="submitting"
          @click="handleSubmit"
          class="btn-primary disabled:opacity-50 disabled:pointer-events-none inline-flex items-center gap-2"
        >
          <span
            v-if="submitting"
            class="inline-block size-4 border-2 border-current border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
          {{ submitting ? (isEditMode ? 'Saving…' : 'Creating…') : (isEditMode ? 'Save' : 'Create') }}
        </button>
      </div>
    </div>
  </div>
</template>
